import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Spin } from "antd";
import EnquiryFollowUp from "./EnquiryFollowUp";
import { useLeadDetailsFollowUp } from "../hooks/useManageEnquiry";

/**
 * Route wrapper around the existing EnquiryFollowUp panel.
 *
 * EnquiryFollowUp is a panel component (Compose Mail / Call / Note / Inbox /
 * Scheduled Follow-Ups) that the list screens render inline. Screens that are
 * NOT lists — e.g. Demo Booking detail — need to reach the same activity view,
 * so this exposes it at /leads/:lead_id/followup instead of duplicating the UI.
 *
 * Back behaviour honours where the user came from:
 *   - `state.from` (or ?from=) present → go back there (demo booking page, …)
 *   - otherwise → normal history back, so All Leads / Kanban keep working as before
 */
export default function LeadCommunication() {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Router state survives a normal navigate(); the query param is the fallback
  // for hard reloads and shared links.
  const from = location.state?.from || searchParams.get("from") || null;

  const { data: leadDetails, isLoading, isError } = useLeadDetailsFollowUp(lead_id);
  const [enquiry, setEnquiry] = useState(null);

  useEffect(() => {
    if (!leadDetails) return;
    setEnquiry({
      id:      leadDetails.id,
      name:    leadDetails.name,
      email:   leadDetails.email,
      phone:   leadDetails.phone,
      company: leadDetails.company,
      address: leadDetails.address,
      message: leadDetails.message,
      status:
        leadDetails.is_positive === 1 ? "Positive"
        : leadDetails.is_positive === 2 ? "Negative"
        : "Neutral",
      assignToId:     leadDetails.assign_to      || null,
      assignedToName: leadDetails.assign_to_name || "Unassigned",
      stage:          leadDetails.stage_name,
      followUps:      leadDetails.follow_ups     || [],
    });
  }, [leadDetails]);

  const handleBack = () => {
    if (from) {
      // replace, not push — otherwise the origin page ends up stacked on top of
      // this one and its own Back would bounce the user right back here.
      navigate(from, { replace: true });
      return;
    }
    // Deep link / no history to pop → fall back to the leads list rather than
    // navigating out of the app.
    if (window.history.length > 1) navigate(-1);
    else navigate("/cms/manageenquiry");
  };

  if (isLoading || (!enquiry && !isError)) {
    return (
      <div className="main-content"><div className="page-content">
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <Spin size="large" />
        </div>
      </div></div>
    );
  }

  if (isError || !enquiry) {
    return (
      <div className="main-content"><div className="page-content">
        <div style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#ef4444" }}>
            Failed to load this lead
          </div>
          <button
            onClick={handleBack}
            style={{ marginTop: 16, padding: "8px 20px", borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", cursor: "pointer" }}
          >
            Go Back
          </button>
        </div>
      </div></div>
    );
  }

  // EnquiryFollowUp is a full-screen takeover (height:100vh with its own Back
  // breadcrumb), so it must sit ABOVE the app chrome — not inside .main-content,
  // which squeezes it next to the sidebar and navbar. Same fixed-overlay wrapper
  // that All Leads uses to open this panel.
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1050,
        background: "#f1f5f9",
        overflow: "auto",
      }}
    >
      <EnquiryFollowUp enquiry={enquiry} onClose={handleBack} />
    </div>
  );
}
